// What we will be able to answer about this screen once it is live.
//
// The numbers that started this redesign came from RUM: about a quarter of
// carrier searches ended in a selection, and 38.8% of VoE requests were created
// with no contact method at all. Those are the two things worth measuring
// again, so the events here are chosen to answer exactly them — did the picker
// get used, and did a contact survive the flow — rather than to describe every
// gesture.

import { useCallback, useMemo } from 'react'

import {
  leftRegistryAttributes,
  type LeftRegistry,
  type SearchOutcome,
} from 'src/components/Inputs/CarrierAutocomplete/searchTelemetry'
import { useCarrierSearchTelemetry } from 'src/components/Inputs/CarrierAutocomplete/useCarrierSearchTelemetry'

import type { ContactChoice } from './entryFlow'

export type { LeftRegistry, SearchOutcome }

const COMPONENT = 'EmploymentHistory'
const SURFACE = 'driver_application'

export interface EmploymentTelemetry {
  pickerOpened: (from: 'gap' | 'empty_page' | 'change_employer') => void
  searchResults: (outcome: SearchOutcome) => void
  carrierSelected: (params: { rank: number; dotNumber?: string | null }) => void
  manualEntry: (params: LeftRegistry) => void
  /**
   * The driver said it was not a driving or DOT-regulated job. Its own event,
   * so a job with no carrier to find does not count as a missed pick.
   */
  nonDrivingJob: (params: LeftRegistry) => void
  /** "N/A"-style input taken to the time-between-jobs entry instead. */
  gapNudgeAccepted: (params: { query: string }) => void
  /** Called when a search is left behind. Fires once per interaction, or not at all. */
  searchAbandoned: () => void
  contactAdded: (
    contact: ContactChoice,
    params?: { platformOnly?: boolean }
  ) => void
  contactSkipped: (params: { knownCount: number }) => void
  /**
   * The contact step was passed without asking, because the employer answers
   * verifications only through a platform. Deliberately not a skip: counting it
   * as one made the capture rate fall every time a carrier was marked.
   */
  contactNotNeeded: (params: { dotNumber?: string | null }) => void
  answersCopied: () => void
  entrySaved: (params: {
    kind: 'employment' | 'unemployment'
    isNew: boolean
    carrierSelected: boolean
    hasContact: boolean
    /** The employer answers verifications only through a platform. */
    contactNotNeeded?: boolean
    /** Saved as not a driving or DOT-regulated job, so no carrier expected. */
    nonDrivingJob?: boolean
  }) => void
  entryRemoved: (params: { kind: 'employment' | 'unemployment' }) => void
  continuedWithGaps: (params: {
    gapCount: number
    uncoveredMonths: number
    enforced: boolean
  }) => void
}

export const useEmploymentTelemetry = (): EmploymentTelemetry => {
  const registry = useCarrierSearchTelemetry({
    component: COMPONENT,
    surface: SURFACE,
  })
  const { track, resolved } = registry

  const manualEntry = useCallback(
    (params: LeftRegistry) =>
      registry.manualEntry(params, { non_driving_job: false }),
    [registry]
  )

  const nonDrivingJob = useCallback(
    (params: LeftRegistry) => {
      resolved()
      track('fmcsa_non_driving_job', {
        ...leftRegistryAttributes(params),
        non_driving_job: true,
      })
    },
    [resolved, track]
  )

  const gapNudgeAccepted = useCallback(
    ({ query }: { query: string }) => {
      resolved()
      track('fmcsa_gap_nudge_accepted', { search_term: query })
    },
    [resolved, track]
  )

  return useMemo(
    () => ({
      pickerOpened: (from) =>
        track('fmcsa_picker_opened', { opened_from: from }),

      searchResults: registry.searchResults,

      carrierSelected: ({ rank, dotNumber }) =>
        registry.carrierSelected({ rank, dotNumber }),

      manualEntry,

      nonDrivingJob,

      gapNudgeAccepted,

      searchAbandoned: registry.searchAbandoned,

      contactAdded: (contact, params = {}) => {
        track('voe_contact_added', {
          // Given anyway, for an employer that did not need one
          platform_only: Boolean(params.platformOnly),
          // The split that says whether pre-selecting a known contact is what
          // fixed the 38.8%, or whether drivers are typing their own
          source: contact.source,
          has_phone: Boolean(contact.phone),
          has_email: Boolean(contact.email),
          has_name: Boolean(contact.name),
        })
      },

      contactSkipped: ({ knownCount }) => {
        track('voe_contact_skipped', {
          known_count: knownCount,
          // Skipping with none offered is a different problem from skipping
          // past contacts we already held
          had_options: knownCount > 0,
        })
      },

      contactNotNeeded: ({ dotNumber }) => {
        track('voe_contact_not_needed', {
          // No platform name: the driver's client is never told it. The
          // server's voe_created carries employer_platform for the breakdown.
          dot_number: dotNumber ?? null,
        })
      },

      answersCopied: () => track('compliance_answers_copied', {}),

      entrySaved: (params) => {
        track('employment_entry_saved', {
          entry_kind: params.kind,
          is_new: params.isNew,
          carrier_selected: params.carrierSelected,
          has_contact: params.hasContact,
          contact_not_needed: Boolean(params.contactNotNeeded),
          non_driving_job: Boolean(params.nonDrivingJob),
        })
      },

      entryRemoved: (params) =>
        track('employment_entry_removed', { entry_kind: params.kind }),

      continuedWithGaps: (params) => {
        track('employment_continue_with_gaps', {
          gap_count: params.gapCount,
          uncovered_months: params.uncoveredMonths,
          enforced: params.enforced,
        })
      },
    }),
    [registry, track, manualEntry, nonDrivingJob, gapNudgeAccepted]
  )
}
